import {
    ArrowLeft,
    Search,
    Share2,
    ShoppingCart,
    User,
    Home as HomeIcon,
    Globe,
    Menu,
    Heart,
} from "lucide-react";
import {
    useLocation,
    useNavigate,
    useParams,
    Link,
    useSearchParams,
} from "react-router-dom";
import { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import MobileMenuDropdown from "../navbar/MobileMenuDropdown";
import type { ShopHeaderProps } from "@/types/shop-type";
import { useAuth } from "@/layouts/AuthContext";
import { ServerStorage } from "@/constants/app-data";
import { UserContext } from "@/contexts/user-context";

export default function ShopHeader({
    title,
    showBack = false,
    onBack,
    showHome = false,
    showMenu = false,
    showLanguage = false,
    showSearch = false,
    showShare = false,
    onShare,
    showFavorite = false,
    showCart = false,
    showProfile = false,
    transparent = false,
    showFunction = false,
    statusFunc = false,
    disableBtnFunc = false,
    labelFunc,
    iconFunc,
    onClickFunction,
}: ShopHeaderProps) {
    const navigate = useNavigate();
    const { lang } = useParams();
    const baseRoute = `/${lang || "id"}`;
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [searchParams] = useSearchParams();
    const searchParamValue = searchParams.get("search") || "";
    const [prevSearchParam, setPrevSearchParam] = useState(searchParamValue);
    const [search, setSearch] = useState(searchParamValue);

    if (searchParamValue !== prevSearchParam) {
        setPrevSearchParam(searchParamValue);
        setSearch(searchParamValue);
    }

    const Icon = iconFunc ?? null;
    const { t, i18n } = useTranslation();
    const location = useLocation();

    const { isAuthenticated, user } = useAuth();
    const { cart, favorite } = useContext(UserContext);
    const avatar = user?.photo
        ? `${ServerStorage}${user.photo}`
        : user?.googlePhoto
          ? user.googlePhoto
          : null;

    // Simple language toggler for mockup
    const cycleLanguage = () => {
        const langs = ["id", "en", "my"];
        const currentIdx = langs.indexOf(lang || "id");
        const nextLang = langs[(currentIdx + 1) % langs.length];
        i18n.changeLanguage(nextLang);
        localStorage.setItem("preferredLang", nextLang);
        navigate(`/${nextLang}${window.location.pathname.substring(3)}`, {
            replace: true,
            state: location.state,
        });
    };

    const [isDark, setIsDark] = useState(() => {
        const savedTheme = localStorage.getItem("theme");
        if (savedTheme === "dark") {
            document.documentElement.classList.add("dark");
            return true;
        } else {
            document.documentElement.classList.remove("dark");
            return false;
        }
    });

    const toggleMenu = () => setIsMenuOpen(!isMenuOpen);
    const toggleTheme = () => {
        if (isDark) {
            document.documentElement.classList.remove("dark");
            localStorage.setItem("theme", "light");
            setIsDark(false);
        } else {
            document.documentElement.classList.add("dark");
            localStorage.setItem("theme", "dark");
            setIsDark(true);
        }
    };

    const changeLanguage = (newLang: string) => {
        i18n.changeLanguage(newLang);
        localStorage.setItem("preferredLang", newLang);
        const newPath = location.pathname.replace(`/${lang}`, `/${newLang}`);
        navigate(newPath + location.search, {
            replace: true,
            state: location.state,
        });
    };

    const languages = [
        { code: "id", label: "Indonesia" },
        { code: "en", label: "English" },
        { code: "my", label: "Melayu" },
    ];

    const handleSearch = (value: string) => {
        if (value.trim()) {
            navigate(
                `${baseRoute}/shop?search=${encodeURIComponent(value.trim())}`,
            );
        } else {
            navigate(`${baseRoute}/shop`);
        }
    };

    useEffect(() => {
        const handler = setTimeout(() => {
            const urlSearch = searchParams.get("search") || "";
            if (search !== urlSearch) {
                handleSearch(search);
            }
        }, 500);
        return () => clearTimeout(handler);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [search]);

    return (
        <>
            <header
                className={`md:hidden top-0 z-50 w-full transition-colors duration-300 ${
                    transparent
                        ? "absolute bg-transparent"
                        : "sticky bg-white/90 dark:bg-dark/90 backdrop-blur-md border-b border-gray-100 dark:border-dark-2"
                }`}>
                <div className="max-w-md mx-auto px-4 py-2 h-14 flex items-center justify-between gap-3">
                    <div className="flex items-center gap-1">
                        {showProfile && (
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(
                                        `${baseRoute}/${isAuthenticated ? "profile" : "login"}`,
                                    )
                                }
                                className={`mr-1 ${isAuthenticated && avatar ? "p-0.5" : "p-2"} rounded-full transition flex items-center justify-center border
                                    ${
                                        transparent
                                            ? "bg-black/20 text-white backdrop-blur-sm border-white/20"
                                            : "bg-gold/10 text-dark dark:text-white border-gold/40"
                                    }
                                    hover:scale-105 hover:bg-black/10 dark:hover:bg-white/10
                                `}>
                                {isAuthenticated ? (
                                    <>
                                        {avatar ? (
                                            <img
                                                src={avatar}
                                                alt="avatar"
                                                className="w-8 h-8 rounded-full object-cover"
                                            />
                                        ) : (
                                            <div className="w-5 h-5 rounded-full flex items-center justify-center bg-white/20">
                                                <User size={14} />
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <div className="w-5 h-5 rounded-full flex items-center justify-center">
                                        <User size={20} />
                                    </div>
                                )}
                            </button>
                        )}
                        {showBack && (
                            <button
                                type="button"
                                onClick={() =>
                                    onBack ? onBack() : navigate(-1)
                                }
                                className={`p-2 -ml-2 rounded-full ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <ArrowLeft size={20} />
                            </button>
                        )}
                        {showHome && (
                            <Link
                                to={baseRoute}
                                className={`p-2 -ml-2 rounded-full ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <HomeIcon size={20} />
                            </Link>
                        )}
                    </div>

                    {title ? (
                        <h1
                            className={`font-semibold flex-1 ${transparent ? "text-white" : "text-dark dark:text-white"} ${showBack || showHome || showProfile ? "text-center" : ""}`}>
                            {title}
                        </h1>
                    ) : (
                        showSearch && (
                            <div className="flex-1 relative">
                                <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
                                    <Search
                                        size={16}
                                        className="text-gray-400"
                                    />
                                </div>
                                <input
                                    type="text"
                                    placeholder={t("nav.search_placeholder")}
                                    className={`w-full h-9 pl-9 pr-4 rounded-full text-sm outline-none transition-colors ${
                                        transparent
                                            ? "bg-white/90 focus:bg-white text-black"
                                            : "bg-[#F5F5F5] dark:bg-dark-2 text-dark dark:text-white focus:ring-1 focus:ring-gold"
                                    }`}
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                    onKeyDown={(e) => {
                                        if (e.key === "Enter") {
                                            handleSearch(search);
                                        }
                                    }}
                                />
                            </div>
                        )
                    )}

                    <div className="flex items-center gap-1">
                        {showLanguage && (
                            <button
                                type="button"
                                onClick={cycleLanguage}
                                className={`flex items-center gap-1 px-2 py-1.5 rounded-full text-xs font-medium uppercase ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white bg-black/5 dark:bg-white/5"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <Globe size={14} /> {lang || "id"}
                            </button>
                        )}
                        {showSearch && title && (
                            <button
                                type="button"
                                className={`p-2 rounded-full ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <Search size={20} />
                            </button>
                        )}
                        {showShare && (
                            <button
                                type="button"
                                onClick={onShare}
                                className={`p-2 rounded-full ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <Share2 size={20} />
                            </button>
                        )}
                        {showFavorite && (
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(`${baseRoute}/favorites`)
                                }
                                className={`p-2 rounded-full relative ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <Heart size={20} />
                                <span className="absolute top-0 right-0 bg-red-500 text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                    {favorite?.length || 0}
                                </span>
                            </button>
                        )}
                        {showCart && (
                            <button
                                type="button"
                                onClick={() => navigate(`${baseRoute}/cart`)}
                                className={`p-2 rounded-full relative ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <ShoppingCart size={20} />
                                <span className="absolute top-0 right-0 bg-gold text-white text-[9px] font-bold w-4 h-4 rounded-full flex items-center justify-center">
                                    {cart?.length || 0}
                                </span>
                            </button>
                        )}
                        {showFunction && (
                            <button
                                type="button"
                                onClick={onClickFunction}
                                disabled={disableBtnFunc}
                                className="flex items-center justify-center gap-1.5 text-xs font-bold text-gold py-1 cursor-pointer">
                                {Icon && (
                                    <Icon
                                        size={14}
                                        className="relative -top-px"
                                    />
                                )}
                                {labelFunc
                                    ? labelFunc
                                    : statusFunc
                                      ? "Selesai"
                                      : "Edit"}
                            </button>
                        )}
                        {showMenu && (
                            <button
                                type="button"
                                onClick={() => setIsMenuOpen(!isMenuOpen)}
                                className={`p-2 -mr-2 rounded-full ${transparent ? "bg-black/20 text-white backdrop-blur-sm" : "text-dark dark:text-white"} hover:bg-black/10 dark:hover:bg-white/10 transition`}>
                                <Menu size={20} />
                            </button>
                        )}
                    </div>
                </div>
                <MobileMenuDropdown
                    isOpen={isMenuOpen}
                    isDark={isDark}
                    languages={languages}
                    toggleTheme={toggleTheme}
                    changeLanguage={changeLanguage}
                    onClose={toggleMenu}
                />
            </header>
        </>
    );
}
